import { motion } from 'motion/react';
import { Container, Kicker, Mark } from '@/components/primitives';
import { Badge } from '@/components/ui/badge';
import { CTA } from '@/components/CTA';

const VALORI = [
  {
    n: '01',
    t: 'Il cantiere prima del software.',
    d: 'Costruiamo attorno alla realtà del cantiere, non lo costringiamo ad adattarsi al gestionale.',
  },
  {
    n: '02',
    t: "L'AI alla portata di tutti.",
    d: "Moltiplichiamo il potenziale delle persone e mettiamo l'intelligenza artificiale alla portata di ogni impresa — non solo dei grandi gruppi.",
  },
  {
    n: '03',
    t: 'I fatti prima delle slide.',
    d: 'La prova è il prodotto che gira su commesse reali. Prima in produzione, poi il racconto.',
  },
  {
    n: '04',
    t: 'Costruiamo dal Sud, per scelta.',
    d: 'Dimostriamo che un prodotto d\'eccellenza si può costruire nel Mezzogiorno. Vogliamo essere il motivo per cui i migliori scelgono di restare — o di tornare.',
  },
  {
    n: '05',
    t: 'Giovani, ambiziosi, rigorosi.',
    d: 'Una squadra giovane, con poche gerarchie e tanta voglia di dimostrare, unita alla serietà di chi un prodotto lo manda davvero in produzione.',
  },
];

export default function ChiSiamoPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(50% 50% at 50% -10%, color-mix(in oklch, var(--primary) 18%, transparent), transparent 70%)',
          }}
        />
        <Container className="relative">
          <Badge variant="soft">· Chi siamo</Badge>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="boss-display mt-6 max-w-[20ch] text-balance text-[48px] md:text-[88px]"
          >
            L'ERP AI-native per <Mark>chi fa cantieristica.</Mark>
          </motion.h1>
        </Container>
      </section>

      {/* Vision */}
      <section className="relative border-t border-border py-24 md:py-32">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[280px_1fr] lg:gap-20">
            <Kicker index="01" label="Vision" />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8 }}
              className="max-w-2xl text-pretty text-[22px] font-light leading-[1.5] tracking-tight text-foreground md:text-[30px]"
            >
              Un settore della cantieristica dove ogni impresa — e ogni anello della sua filiera — lavora su un
              dato strutturato e condiviso, e dove quel dato diventa la leva che moltiplica il valore di ogni
              persona attraverso l'intelligenza artificiale.
              <br />
              <br />
              Vogliamo trasformare il cantiere — oggi ancora territorio di carta, Excel e comunicazioni
              sparse — nel settore più data-driven d'Italia.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* Mission */}
      <section className="relative border-t border-border py-24 md:py-32">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[280px_1fr] lg:gap-20">
            <Kicker index="02" label="Mission" />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8 }}
              className="max-w-2xl text-pretty text-[18px] leading-[1.65] text-fg-2"
            >
              Diamo alle imprese che lavorano nei cantieri, dalla più piccola alla più strutturata, un'unica piattaforma dove far
              vivere tutte le procedure di cantiere, rendere trasparenti i rapporti con subappaltatori e
              fornitori, e tenere sotto controllo commesse e margini in tempo reale.
              <br />
              <br />
              E rendiamo l'intelligenza artificiale accessibile a chi finora non poteva permettersela:
              cantiere dopo cantiere costruiamo la banca dati strutturata che moltiplica il potenziale di ogni
              persona in azienda.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* Valori */}
      <section className="relative border-t border-border py-24 md:py-32">
        <Container>
          <Kicker index="03" label="I nostri valori" />
          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-2">
            {VALORI.map((v, i) => (
              <motion.div
                key={v.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: i * 0.06, duration: 0.7 }}
                className={`bg-background p-8 ${i === VALORI.length - 1 ? 'md:col-span-2' : ''}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary">{v.n}</div>
                <h3 className="boss-h1 mt-4 text-[22px] leading-[1.15]">{v.t}</h3>
                <p className="mt-3 text-pretty text-[14px] leading-[1.6] text-fg-2">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Signature */}
      <section className="relative border-t border-border py-16">
        <Container>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-fg-disabled">
            BOSS S.r.l. · Abruzzo
          </p>
        </Container>
      </section>

      <CTA />
    </>
  );
}
